import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdCheckCircle, MdOutlineInfo } from "react-icons/md";
import Button from "shared/components/Button";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { PROCESSES } from "utils/api/graphql/queries/processes";
import { PROCESS_TYPES } from "utils/constants";

export default function JourneySection({ onConfigChange, contract }) {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.COMPANY);
  const JOURNEY_SECTION = t("JOURNEY_SECTION", { returnObjects: true });

  const { data: processesData, loading: processesLoading } = useQuery(
    PROCESSES,
    {
      variables: {
        isIn: { type: [PROCESS_TYPES.B2C, PROCESS_TYPES.COLLECTIVE] },
        take: null,
        orderBy: [{ name: "ASC" }],
      },
    },
  );

  const [updateContract, { loading: saving }] = useMutation(
    graphql.mutations.UPDATE_CONTRACT,
  );

  const processes = processesData?.processes?.data ?? [];
  const initialProcessId = contract?.subjects?.processId || null;
  const [selectedProcessId, setSelectedProcessId] = useState(initialProcessId);
  useEffect(() => {
    setSelectedProcessId(initialProcessId);
  }, [initialProcessId]);
  const selectedProcess = processes.find(
    (processItem) => processItem.id === selectedProcessId,
  );
  // const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  // const [selectedJourneyForDetails, setSelectedJourneyForDetails] =
  //   useState(null);

  // function openDetailsDrawer(journey) {
  //   setSelectedJourneyForDetails(journey);
  //   setDetailsDrawerOpen(true);
  // }

  const isComplete = Boolean(contract?.id && selectedProcessId);

  function handleProcessSelection(processId) {
    setSelectedProcessId(processId);
    onConfigChange?.({
      ...contract,
      subjects: {
        ...contract?.subjects,
        processId,
      },
    });
  }

  async function handleSave() {
    if (!isComplete) {
      return;
    }

    const existingSubjects = contract?.subjects || {};

    try {
      await updateContract({
        variables: {
          where: { id: contract.id },
          data: {
            subjects: {
              ...existingSubjects,
              processId: selectedProcessId,
            },
          },
        },
      });
    } catch (error) {
      console.error("Failed to update contract subjects", error);
    }
  }

  return (
    <div className="company__space-y-8">
      <div className="company__info-box company__p-4 company__flex company__gap-3">
        <MdOutlineInfo
          className="company__w-5 company__h-5 company__text-blue-600 company__flex-shrink-0"
          style={{ marginTop: "0.125rem" }}
        />
        <div className="company__text-sm company__text-blue-900">
          <p className="company__font-medium company__mb-1">
            {JOURNEY_SECTION.INFO_BOX.TITLE}
          </p>
          <p>{JOURNEY_SECTION.INFO_BOX.DESCRIPTION}</p>
        </div>
      </div>

      <div>
        <h3 className="company__text-lg company__font-semibold company__text-slate-900 company__mb-4">
          {JOURNEY_SECTION.HEADER.TITLE}
        </h3>
        <p className="company__text-sm company__text-slate-600 company__mb-6">
          {JOURNEY_SECTION.HEADER.SUBTITLE}
        </p>

        <div className="company__grid company__grid-cols-1 company__md-grid-cols-2 company__gap-4">
          {processesLoading ? (
            <div className="company__col-span-full company__p-6 company__bg-slate-50 company__border company__border-slate-200 company__rounded-lg company__text-center company__text-slate-600">
              {JOURNEY_SECTION.STATES.LOADING}
            </div>
          ) : processes.length === 0 ? (
            <div className="company__col-span-full company__p-6 company__bg-slate-50 company__border company__border-slate-200 company__rounded-lg company__text-center company__text-slate-600">
              {JOURNEY_SECTION.STATES.EMPTY}
            </div>
          ) : (
            processes.map((processItem) => {
              const isSelected = selectedProcessId === processItem.id;

              return (
                <div
                  key={processItem.id}
                  className={`company__relative company__p-5 company__border-2 company__rounded-lg company__cursor-pointer company__transition-all ${
                    isSelected
                      ? "company__border-blue-500 company__bg-blue-50"
                      : "company__border-slate-200 company__hover-border-blue-300 company__hover-bg-slate-50"
                  }`}
                  onClick={() => handleProcessSelection(processItem.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleProcessSelection(processItem.id);
                    }
                  }}
                >
                  {isSelected && (
                    <div className="company__absolute company__top-3 company__right-3">
                      <MdCheckCircle className="company__w-6 company__h-6 company__text-blue-600" />
                    </div>
                  )}

                  <div className="company__mb-3">
                    <div className="company__flex company__items-start company__gap-2 company__mb-2">
                      <h4 className="company__font-semibold company__text-slate-900 company__text-base company__leading-tight company__flex-1">
                        {processItem.name}
                      </h4>
                      {/* Bouton de détail (icône oeil) désactivé pour le moment */}
                      {/* <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openDetailsDrawer(processItem);
                        }}
                        className="company__text-blue-600 company__hover-text-blue-700 company__hover-bg-blue-50 company__p-2 company__rounded company__transition-colors"
                        title="Voir plus de détails"
                      >
                        <Eye className="company__w-4 company__h-4" />
                      </button> */}
                    </div>
                    <p className="company__text-sm company__text-slate-600 company__leading-relaxed">
                      {processItem.description ||
                        JOURNEY_SECTION.GENERIC.NO_DESCRIPTION}
                    </p>
                  </div>

                  {/* Anciennes informations d'affichage (mandat, dispense, modalité) conservées en référence :
                  <div className="company__space-y-2" style={{ paddingTop: '0.75rem', borderTop: '1px solid #e2e8f0' }}>
                    <div className="company__flex company__items-center company__gap-2 company__text-sm">
                      <div className="company__w-5 company__h-5 company__rounded company__flex company__items-center company__justify-center">✓</div>
                      <span className="company__text-slate-700">Mandat de prélèvement</span>
                    </div>
                    <div className="company__flex company__items-center company__gap-2 company__text-sm">
                      <div className="company__w-5 company__h-5 company__rounded company__flex company__items-center company__justify-center">✓</div>
                      <span className="company__text-slate-700">Dispense d'adhésion</span>
                    </div>
                    <div className="company__flex company__items-center company__gap-2 company__text-sm">
                      <div className="company__w-5 company__h-5 company__rounded company__flex company__items-center company__justify-center">=</div>
                      <span className="company__text-slate-700">Garantie uniforme pour les foyers</span>
                    </div>
                  </div>
                  */}
                </div>
              );
            })
          )}
        </div>
      </div>

      {selectedProcess && (
        <div className="company__p-5 company__bg-gradient-to-r company__from-blue-50 company__to-green-50 company__border company__border-blue-200 company__rounded-lg">
          <div className="company__flex company__items-start company__gap-3">
            <MdCheckCircle
              className="company__w-6 company__h-6 company__text-green-600 company__flex-shrink-0"
              style={{ marginTop: "0.125rem" }}
            />
            <div>
              <h4 className="company__font-semibold company__text-slate-900 company__mb-2">
                {JOURNEY_SECTION.SELECTED.TITLE}
              </h4>
              <p className="company__text-sm company__text-slate-700 company__mb-3">
                {selectedProcess.name}
              </p>
              <p className="company__text-sm company__text-slate-700">
                {selectedProcess.description ||
                  JOURNEY_SECTION.GENERIC.NO_DESCRIPTION}
              </p>
              {/* Ancienne synthèse détaillée conservée en commentaire :
              <div className="company__grid company__grid-cols-1 company__md-grid-cols-3 company__gap-3 company__text-sm">
                <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }} className="company__px-3 company__py-2 company__rounded">
                  <span className="company__text-slate-600">Mandat SEPA : </span>
                  <span className="company__font-medium company__text-slate-900">Oui</span>
                </div>
                <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }} className="company__px-3 company__py-2 company__rounded">
                  <span className="company__text-slate-600">Dispense : </span>
                  <span className="company__font-medium company__text-slate-900">Oui</span>
                </div>
                <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }} className="company__px-3 company__py-2 company__rounded">
                  <span className="company__text-slate-600">Modalité : </span>
                  <span className="company__font-medium company__text-slate-900">Uniforme</span>
                </div>
              </div>
              */}
            </div>
          </div>
        </div>
      )}

      {/* Ancien volet de détails conservé en commentaire :
      {detailsDrawerOpen && selectedJourneyForDetails && (
        <div className="company__fixed company__inset-0 company__bg-opacity-50 company__flex company__items-center company__justify-center company__z-50">
          <div className="company__bg-white company__rounded-xl company__w-full company__max-w-2xl company__max-h-90vh company__overflow-y-auto">
            ...
          </div>
        </div>
      )}
      */}
      <div className="company__pt-6 company__sticky company__bottom-0 company__bg-white">
        <Button
          block
          onClick={handleSave}
          disabled={saving || !isComplete}
          type="primary"
        >
          {saving
            ? JOURNEY_SECTION.ACTIONS.SAVING
            : JOURNEY_SECTION.ACTIONS.SAVE}
        </Button>
      </div>
    </div>
  );
}
